import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  effects as companyEffects,
  selectors as companySelectors,
} from 'lib/entities/company';
import SEO from 'global/SEO';
import dynamic from 'next/dynamic';
import useProtectedRoute from 'hooks/useProtectedRoute';
import usePaywallCompany from 'features/paywall/store/useCompany';
import PageLoader from 'global/Loading/PageLoader';
import { clevertapEvent } from 'utils/clevertap';
import { CLEVERTAP_EVENT_NAME } from 'constants/clevertap';
import { selectors as credentialSelectors } from 'lib/entities/credentials';
import { selectors as profileSelectors } from 'lib/entities/profile';
import QueryProvider from 'global/QueryProvider';
import {
  ITabItems,
  TabListNameEnums,
  CompanyActionEnums,
  IClevertapEvent,
  IClevertapEventReturn,
  CompanyInfo,
} from '../../../@types/company.d';
import Navbar from './Navbar';
import Hero from './Hero';
import {
  SOURCE_MENU,
  TAB_ANALYST_NEW_NAME,
  TAB_LIST,
  TAB_LIST_NAME,
} from './constants';
import companyProfile from './slice';
import { clevertapCompanyViewed } from './index.clevertap';
import { useCompanyStore } from './store';

const NotFound = dynamic(() => import('global/NotFound'), {
  ssr: false,
});
const Stream = dynamic(() => import('./Stream'), {
  ssr: false,
});
const Insider = dynamic(() => import('./Insider'), {
  ssr: false,
});
const Seasonality = dynamic(() => import('./Seasonality'), {
  ssr: false,
});
const Keystat = dynamic(() => import('./Keystat'), {
  ssr: false,
});
const Comparison = dynamic(() => import('./Comparison'), {
  ssr: false,
});
const Financial = dynamic(() => import('./Financial'), {
  ssr: false,
});
const Fundachart = dynamic(() => import('./Fundachart'), {
  ssr: false,
});
const Paywall = dynamic(() => import('../paywall/Wrapper/Company'), {
  ssr: false,
});
const Analyst = dynamic(() => import('./Analyst'), {
  ssr: false,
});
const Profile = dynamic(() => import('./Profile'), {
  ssr: false,
});
const Consensus = dynamic(() => import('./Analyst/Consensus'), {
  ssr: false,
});
const CorpAction = dynamic(() => import('./CorpAction'), {
  ssr: false,
});
const FRPage = dynamic(() => import('./FRPage'), {
  ssr: false,
});

const COMPANY_SYMBOL_COMPONENTS = {
  [TAB_LIST_NAME.CONSENSUS]: Consensus,
};

const COMPANY_SYMBOL_PRO_COMPONENTS = {
  [TAB_LIST_NAME.KEYSTATS]: Keystat,
  [TAB_LIST_NAME.FINANCIALS]: Financial,
  [TAB_LIST_NAME.FUNDACHART]: Fundachart,
  [TAB_LIST_NAME.SEASONALITY]: Seasonality,
  [TAB_LIST_NAME.COMPARISON]: Comparison,
  [TAB_LIST_NAME.ANALYST]: Analyst,
  [TAB_LIST_NAME.INSIDER]: Insider,
  [TAB_LIST_NAME.PROFILE]: Profile,
  [TAB_LIST_NAME.CORP_ACTION_NEW_NAME]: CorpAction,
};

interface CompanyProps {
  symbol: string;
  tab?: string;
}

function Company({ symbol, tab }: CompanyProps) {
  const { isLoaded } = useProtectedRoute();
  const dispatch = useDispatch();
  const {
    detail: { data: companyData, error },
  } = useSelector(companySelectors);
  const { isLoggedIn } = useSelector(credentialSelectors);
  const { data } = useSelector(profileSelectors);
  const { id: userId, country } = data;
  const [activeTab, setActiveTab] = useState<string>('');
  const setCompanyLoading = useCompanyStore(({ setLoading }) => setLoading);

  const paywallTab = tab === TAB_LIST_NAME.BUY ? undefined : tab;

  usePaywallCompany(paywallTab, symbol);

  useEffect(() => {
    setCompanyLoading(true);
    dispatch(companyProfile.actions.updateLoadingState({ isLoading: true }));
  }, []);

  useEffect(() => {
    if (symbol) {
      const getCompanyDetail = async () => {
        // @ts-ignore
        const {
          // @ts-ignore
          payload: { error: companyDetailError, data: companyDetailData },
        } = await dispatch(companyEffects.getCompanyDetail(symbol));

        // @ts-ignore
        if (!companyDetailError && companyDetailData) {
          clevertapCompanyViewed(companyDetailData as CompanyInfo);
        }
      };

      getCompanyDetail();
    }
  }, [dispatch, symbol]);

  useEffect(() => {
    if (tab) {
      setCompanyLoading(true);
      dispatch(companyProfile.actions.updateLoadingState({ isLoading: true }));
      setActiveTab(tab);
    }
  }, [tab]);

  if (!symbol || !companyData[symbol]) {
    return (
      <>
        <SEO />
        <div />
      </>
    );
  }

  if (error) {
    return <NotFound />;
  }

  const currentCompany = companyData[symbol];

  const streamTabs: Array<TabListNameEnums> = [
    TAB_LIST_NAME.HOME,
    TAB_LIST_NAME.STREAM,
    TAB_LIST_NAME.NEWS,

    TAB_LIST_NAME.BUY,
  ];
  const proTabs: Array<TabListNameEnums> = [
    TAB_LIST_NAME.FUNDACHART,
    TAB_LIST_NAME.FINANCIALS,
    TAB_LIST_NAME.KEYSTATS,
    TAB_LIST_NAME.COMPARISON,
    TAB_LIST_NAME.SEASONALITY,
    TAB_LIST_NAME.ANALYST,
    TAB_LIST_NAME.INSIDER,
    TAB_LIST_NAME.PROFILE,
    TAB_LIST_NAME.CORP_ACTION_NEW_NAME,
  ];

  let tabs: Array<string> = companyData[symbol]?.tabs || [];
  if (country === 'MY' && tabs.length > 0) tabs = ['stream'];

  const companyTabs: Array<ITabItems> = tabs
    .filter((value) => value !== TAB_LIST_NAME.NEWS)
    .map((value) => {
      let newValue = value;
      if (value === TAB_ANALYST_NEW_NAME) {
        newValue = TAB_LIST_NAME.ANALYST;
      }

      return TAB_LIST[newValue];
    });

  const isTabsIncluded = (
    tabList: Array<TabListNameEnums>,
    name: string,
  ): boolean => Object.values(tabList).includes(name as TabListNameEnums);

  const renderTabContent = (tabName: string, isPro = false): JSX.Element => {
    const companySymbolComponentList = isPro
      ? COMPANY_SYMBOL_PRO_COMPONENTS
      : COMPANY_SYMBOL_COMPONENTS;
    const Component = companySymbolComponentList?.[tabName];
    return Component ? (
      <Component
        symbol={symbol}
        companyType={companyData[symbol]?.type_company}
      />
    ) : (
      <></>
    );
  };

  const clevertapEventAction = (name: string): IClevertapEventReturn => {
    const clevertapEventActionEnum = CompanyActionEnums;
    const newName = name.toUpperCase();
    const action: string = clevertapEventActionEnum[newName];

    const isFCA = companyData[symbol]?.notation?.some(
      (item) => item?.notation_code === 'X',
    );
    const notation: string[] = companyData[symbol]?.notation?.map(
      (item) => item?.notation_code,
    );

    const eventReturn: IClevertapEventReturn = {
      eventName: CLEVERTAP_EVENT_NAME.COMPANY_MENU,
      eventPayload: {
        Action: action,
        Stock: symbol,
        Company_Type: companyData[symbol]?.type_company,
        Is_FCA: isFCA,
      },
    };

    if (notation.length > 0) {
      eventReturn.eventPayload.Special_Notation = notation.toString();
    }

    return eventReturn;
  };

  /**
   * Send CleverTap Event. "analyst" & "seasonality" event not same as the others.
   * @param {Object} eventParams
   * @param {
   * "analyst" | "stream" | "news" | "keystats" |
   * "financials" | "fundachart" | "seasonality" |
   * "chartbit" | "comparison" | "insider" | "profile" |
   * "corp.action"
   * } eventParams.tabPath Company Tab Name
   * @param {number} eventParams.timeout timeout before sending event in ms
   */
  const sendCleverTapEvent = ({ tabPath, timeout = 0 }: IClevertapEvent) => {
    const isStreamTabs: boolean = isTabsIncluded(streamTabs, tabPath);
    const newTabPath: string = isStreamTabs ? TAB_LIST_NAME.STREAM : tabPath;
    const { eventName, eventPayload }: IClevertapEventReturn =
      clevertapEventAction(newTabPath);

    if (newTabPath === TAB_LIST_NAME.CHARTBIT) {
      eventPayload.Source = SOURCE_MENU;
    }

    setTimeout(() => {
      try {
        clevertapEvent(eventName, eventPayload);
      } catch (_error) {
        console.error('Error sending CleverTap event');
      }
    }, timeout);
  };

  if (currentCompany?.type_company === 'FR') {
    return (
      <QueryProvider>
        <SEO
          title={`${currentCompany.symbol} ${currentCompany.name} on Stockbit`}
        />
        <PageLoader loaded={isLoaded} />
        <FRPage symbol={symbol} />
      </QueryProvider>
    );
  }

  return (
    <>
      <SEO
        title={`${currentCompany.symbol} ${currentCompany.name} on Stockbit`}
      />
      <PageLoader loaded={isLoaded} />
      <Hero symbol={symbol} />
      <Navbar
        tabs={companyTabs}
        active={activeTab}
        onSendClevertapEvent={sendCleverTapEvent}
        isLoggedIn={isLoggedIn}
        userId={userId}
        className={country.toLowerCase()}
      />
      {isTabsIncluded(streamTabs, activeTab) && (
        <Stream activeNav={activeTab} symbol={symbol} />
      )}

      {isTabsIncluded(proTabs, activeTab) && (
        <Paywall source={activeTab} symbol={symbol}>
          {renderTabContent(activeTab, true)}
        </Paywall>
      )}

      {renderTabContent(activeTab)}
    </>
  );
}

export default Company;
